import axios from 'axios';
import jwt from 'jsonwebtoken';
import { environment } from '../environment/environment';
import { Customer, CustomerAddress, Rating } from './types';
import {
  decrypt,
  getSessionStorage,
  saveSessionStorage,
  encrypt,
} from './functions';

interface ValuesChangePassword {
  userId: string | undefined;
  currentPassword: string;
  newPassword: string;
  confirmNewPassword: string;
}

export const apiCustomer = axios.create({
  baseURL: environment.REACT_APP_API_URL,
});

const getCustomerId = async () => {
  const encryptedToken = getSessionStorage('token') || '';
  const decrytedToken = decrypt(encryptedToken);
  const decodedToken = jwt.decode(decrytedToken);
  return decodedToken?.sub;
};

const updateAuthorization = async () => {
  const encryptedToken = getSessionStorage('token') || '';
  const decrytedToken = decrypt(encryptedToken);
  apiCustomer.defaults.headers.Authorization = `Bearer ${decrytedToken}`;
};

const checkTokenHasExpired = async () => {
  const jwtExpirationDate = new Date(0);
  const dateNow = Date.now();
  const tokenExpirationDate: any = getSessionStorage('tokenExpirationDate');
  if (tokenExpirationDate) {
    jwtExpirationDate.setUTCSeconds(tokenExpirationDate);
    jwtExpirationDate.setSeconds(jwtExpirationDate.getSeconds() - 20);
    if (jwtExpirationDate.getTime() <= dateNow) {
      console.log('Customer token Expired...');
      await refreshToken();
      return true;
    }
  }
  return false;
};

const refreshToken = async () => {
  const encryptedCredentials = getSessionStorage('credentials') || '';
  const decrytedCredentials = decrypt(encryptedCredentials, true);
  const result = await apiCustomer
    .post('auth/token', {
      username: decrytedCredentials.username,
      password: decrytedCredentials.password,
    })
    .then(response => {
      console.log('Refreshing Customer Token...');
      const { token } = response.data;
      const encryptedToken = encrypt(token).toString();
      saveSessionStorage('token', encryptedToken);
      const decodedToken: any = jwt.decode(token);
      const expirationToken = decodedToken.exp;
      saveSessionStorage('tokenExpirationDate', expirationToken);
      return true;
    })
    .catch(err => {
      console.log(err.response);
      return err.response;
    });
  return result;
};

export const sendRequestResetPassword = async (username: string) => {
  const result = await apiCustomer
    .post('/auth/resetPassword/request', {
      username,
    })
    .then(res => res)
    .catch(err => {
      console.log(err.response);
      return err.response;
    });
  return result;
};

export const sendConfirmResetPassword = async (
  userId: string,
  resetToken: string,
) => {
  const result = await apiCustomer
    .post('/auth/resetPassword/confirm', {
      userId,
      resetToken,
    })
    .then(res => res)
    .catch(err => {
      console.log(err.response);
      return err.response;
    });
  return result;
};

export const updateCustomer = async (customer: Customer) => {
  await checkTokenHasExpired();
  updateAuthorization();
  const result = await apiCustomer
    .put(`/customers/${customer.id}`, customer)
    .then(res => res)
    .catch(err => {
      console.log(err.response);
      return err.response;
    });
  return result;
};

export const changePassword = async (
  valuesChangePassword: ValuesChangePassword,
) => {
  await checkTokenHasExpired();
  updateAuthorization();
  const result = await apiCustomer
    .post('/auth/changePassword/', valuesChangePassword)
    .then(res => res)
    .catch(err => {
      console.log(err.response);
      return err.response;
    });
  return result;
};

export const getCustomersAddresses = async () => {
  await checkTokenHasExpired();
  updateAuthorization();
  const customerId = await getCustomerId();
  const result = await apiCustomer
    .get(`/customers/${customerId}/addresses`)
    .then(res => res)
    .catch(err => {
      console.log(err.response);
      return err.response;
    });
  return result;
};

export const registerCustomersAddresses = async (
  customerAddress: CustomerAddress,
) => {
  await checkTokenHasExpired();
  updateAuthorization();
  const customerId = await getCustomerId();
  const result = await apiCustomer
    .post(`/customers/${customerId}/addresses`, customerAddress)
    .then(res => res)
    .catch(err => {
      console.log(err.response);
      return err.response;
    });
  return result;
};

export const deleteCustomersAddresses = async (
  customerAddressId: string | undefined,
) => {
  await checkTokenHasExpired();
  updateAuthorization();
  const customerId = await getCustomerId();
  const result = await apiCustomer
    .delete(`/customers/${customerId}/addresses/${customerAddressId}`)
    .then(res => res)
    .catch(err => {
      console.log(err.response);
      return err.response;
    });
  return result;
};

export const updateCustomersAddresses = async (
  customerAddress: CustomerAddress,
) => {
  await checkTokenHasExpired();
  updateAuthorization();
  const customerId = await getCustomerId();
  const result = await apiCustomer
    .put(
      `/customers/${customerId}/addresses/${customerAddress.id}`,
      customerAddress,
    )
    .then(res => res)
    .catch(err => {
      console.log(err.response);
      return err.response;
    });
  return result;
};

export const getCustomerOrders = async () => {
  await checkTokenHasExpired();
  updateAuthorization();
  const customerId = await getCustomerId();
  const result = await apiCustomer
    .get(`/customers/${customerId}/orders`)
    .then(res => res)
    .catch(err => {
      console.log(err.response);
      return err.response;
    });
  return result;
};

export const getCustomerOrder = async (orderId: string) => {
  await checkTokenHasExpired();
  updateAuthorization();
  const customerId = await getCustomerId();
  const result = await apiCustomer
    .get(`/customers/${customerId}/orders/${orderId}`)
    .then(res => res)
    .catch(err => {
      console.log(err.response);
      return err.response;
    });
  return result;
};
export const sendOrderRating = async (orderId: string, rating: Rating) => {
  await checkTokenHasExpired();
  updateAuthorization();
  const customerId = await getCustomerId();
  const result = await apiCustomer
    .patch(`/customers/${customerId}/orders/${orderId}/ratings`, rating)
    .then(res => res)
    .catch(err => {
      console.log(err.response);
      return err.response;
    });
  return result;
};

export const getCustomerFavorites = async () => {
  await checkTokenHasExpired();
  updateAuthorization();
  const customerId = await getCustomerId();
  const result = await apiCustomer
    .get(`/customers/${customerId}/favorites`)
    .then(res => res)
    .catch(err => {
      console.log(err.response);
      return err.response;
    });
  return result;
};

export const sendCustomerFavorite = async (productId: string | undefined) => {
  await checkTokenHasExpired();
  updateAuthorization();
  const customerId = await getCustomerId();
  const result = await apiCustomer
    .patch(`/customers/${customerId}/favorites/products/${productId}`)
    .then(res => res)
    .catch(err => {
      console.log(err.response);
      return err.response;
    });
  return result;
};
